import { hero, persist, toHub, winStoryFight } from "../app.ts";
import { sfx } from "../audio.ts";
import { ATTACKS, Battle, heroFighter, opponentFighter, type Action, type AttackKind, type BattleEvent, type Side } from "../combat.ts";
import { DEFEAT_LINES } from "../data/story.ts";
import { now, setScene } from "../engine.ts";
import { Actor } from "../render/actor.ts";
import { cellX, drawArena, GROUND } from "../render/arena.ts";
import { Fx } from "../render/fx.ts";
import { gainXp, goldReward, SPECIAL_INFO, xpReward } from "../rules.ts";
import type { Backdrop, Opponent, SpecialId } from "../types.ts";
import { btn, h, mount, setBar, wait } from "../ui/dom.ts";
import { muteButton } from "./common.ts";

const SCALE = 1.45;
const SHOUTS = ["BRING IT!", "BAWK BAWK!", "TOO SLOW!", "IS THAT ALL?", "COME ON!", "WEAK SAUCE!", "NUGGET!"];
const VERBS: Record<AttackKind | "frenzy", string> = { quick: "pecks", normal: "strikes", power: "winds up a Mighty Wallop", frenzy: "frenzies" };

interface Opts {
  opponent: Opponent;
  backdrop: Backdrop;
  story: boolean;
}

export function battleScreen({ opponent, backdrop, story }: Opts): void {
  const me = hero();
  const battle = new Battle(heroFighter(me), opponentFighter(opponent));
  const actors: [Actor, Actor] = [
    new Actor(me.look, me.gear, 1, cellX(battle.f[0].pos)),
    new Actor(opponent.look, opponent.gear, -1, cellX(battle.f[1].pos)),
  ];
  const fx = new Fx();
  const shownHp = [battle.f[0].hp, battle.f[1].hp];
  let front: Side = 0;
  let cheerBoost = 0;
  let busy = true;

  setScene((ctx, t, dt) => {
    cheerBoost = Math.max(0, cheerBoost - dt * 0.5);
    const cheer = Math.min(1, 0.12 + battle.f[0].hype / 140 + cheerBoost);
    const [sx, sy] = fx.offset();
    ctx.save();
    ctx.translate(sx, sy);
    drawArena(ctx, backdrop, t, cheer);
    const back = actors[front === 0 ? 1 : 0];
    back.draw(ctx, t, GROUND, SCALE);
    actors[front].draw(ctx, t, GROUND, SCALE);
    fx.draw(ctx, t, dt);
    ctx.restore();
  });

  const other = (s: Side): Side => (s === 0 ? 1 : 0);
  const headY = (s: Side) => GROUND - actors[s].height * SCALE;
  const midY = (s: Side) => GROUND - actors[s].height * SCALE * 0.5;
  const featherColor = (s: Side) => {
    const look = actors[s].look;
    return look.kind === "bird" ? look.body : "#f5f1e6";
  };

  // ---------- HUD ----------

  function makeHud(side: Side) {
    const f = battle.f[side];
    const hp = h("div", { class: "bar hp" }, h("i", { class: "ghost" }), h("i"), h("b"));
    const en = h("div", { class: "bar en" }, h("i"), h("b"));
    const hype = h("div", { class: "bar hype", title: "Crowd hype: full hype adds 15% damage and a bigger purse" }, h("i"), h("b"));
    const status = h("div", { class: "status" });
    const el = h(
      "div",
      { class: `hud panel ${side === 0 ? "left" : "right"}` },
      h("div", { class: "nm" }, h("span", null, f.name), h("small", null, `Lv ${f.level}${side === 1 && opponent.boss ? " · BOSS" : ""}`)),
      hp,
      en,
      hype,
      status,
    );
    return { el, hp, en, hype, status };
  }
  const huds = [makeHud(0), makeHud(1)] as const;

  function syncHud(side: Side): void {
    const f = battle.f[side];
    const ui = huds[side];
    setBar(ui.hp, shownHp[side]! / f.d.maxHp, `${Math.ceil(shownHp[side]!)} / ${f.d.maxHp}`);
    setBar(ui.en, f.en / f.d.maxEn, `⚡ ${f.en} / ${f.d.maxEn}`);
    setBar(ui.hype, f.hype / 100, f.hype >= 80 ? "🔥 CROWD GOES WILD 🔥" : "");
    const tags: string[] = [];
    if (f.stunned) tags.push("💫 STUNNED");
    if (f.rattled) tags.push("😵 RATTLED");
    if (f.specials.includes("egg")) tags.push(`🥚×${f.eggs}`);
    if (f.roarCd > 0 && f.specials.includes("roar")) tags.push(`📢 ${f.roarCd}`);
    ui.status.replaceChildren(...tags.map((t) => h("span", null, t)));
  }

  function syncAll(): void {
    shownHp[0] = battle.f[0].hp;
    shownHp[1] = battle.f[1].hp;
    syncHud(0);
    syncHud(1);
    refreshActions();
  }

  const logEl = h("div", { class: "log" });
  function log(text: string): void {
    logEl.textContent = text;
    logEl.style.opacity = "1";
  }

  // ---------- actions ----------

  interface Def {
    key: string;
    action: Action;
    label: string;
    sub: () => string;
    cls: string;
  }
  const pct = (n: number) => `${n}%`;
  const attack = (kind: AttackKind, key: string, cls: string): Def => ({
    key,
    action: { type: "attack", kind },
    label: ATTACKS[kind].name,
    cls,
    sub: () => {
      const [lo, hi] = battle.dmgRange(0, kind);
      return `${pct(battle.hitChance(0, kind))} · ${lo}–${hi} · ${ATTACKS[kind].en}⚡`;
    },
  });
  const special = (id: SpecialId, key: string): Def => ({
    key,
    action: { type: "special", id },
    label: SPECIAL_INFO[id].name,
    cls: "blue",
    sub: () => {
      const e = SPECIAL_INFO[id].energy;
      switch (id) {
        case "egg":
          return `${pct(battle.eggChance(0))} · ${battle.eggDmg(0)} dmg · ${e}⚡`;
        case "roar":
          return `${pct(battle.chaChance(0, 45, 15, 80))} stun · ${e}⚡`;
        case "frenzy": {
          const [lo, hi] = battle.dmgRange(0, "frenzy");
          return `3× ${pct(battle.hitChance(0, "frenzy"))} · ${lo}–${hi} · ${e}⚡`;
        }
        case "wind":
          return `+${Math.round(battle.f[0].d.maxHp * 0.35)} HP · once`;
      }
    },
  });

  const groups: Def[][] = [
    [
      { key: "←", action: { type: "retreat" }, label: "◀ Retreat", cls: "wood", sub: () => "back off" },
      { key: "→", action: { type: "advance" }, label: "Advance ▶", cls: "wood", sub: () => `distance ${battle.dist()}` },
    ],
    [attack("quick", "1", ""), attack("normal", "2", ""), attack("power", "3", "red")],
    [
      { key: "T", action: { type: "taunt" }, label: "Taunt", cls: "", sub: () => `${pct(battle.chaChance(0, 55, 15, 92))} · 3⚡` },
      { key: "R", action: { type: "rest" }, label: "Rest", cls: "green", sub: () => `+${Math.round(battle.f[0].d.maxEn * 0.35)}⚡` },
    ],
  ];
  const known = (["egg", "roar", "frenzy", "wind"] as const).filter((id) => me.specials.includes(id));
  if (known.length) groups.push(known.map((id, i) => special(id, String(4 + i))));

  const buttons = groups.flat().map((d) => {
    const sub = h("span", { class: "sub" });
    const b = btn([h("span", { class: "key" }, d.key), d.label, sub], () => void onAction(d.action), d.cls);
    return { d, b, sub };
  });

  function refreshActions(): void {
    for (const { d, b, sub } of buttons) {
      const why = battle.blocked(0, d.action);
      b.disabled = busy || battle.winner !== null || why !== null;
      b.title = why ?? "";
      sub.textContent = why && !busy ? why : d.sub();
    }
  }

  const flee = btn("🏳️ Flee", () => {
    if (busy || battle.winner !== null) return;
    if (!confirm("Flee the arena? This counts as a loss.")) return;
    battle.winner = 1;
    void finish();
  }, "small wood");

  const actionBar = h(
    "div",
    { class: "actions" },
    ...groups.map((g) => h("div", { class: "grp" }, ...buttons.filter((x) => g.includes(x.d)).map((x) => x.b))),
    h("div", { class: "grp" }, flee),
  );

  // ---------- turn resolution ----------

  async function onAction(a: Action): Promise<void> {
    if (busy || battle.winner !== null || battle.blocked(0, a)) return;
    busy = true;
    refreshActions();
    await perform(0, a);
    while (battle.winner === null) {
      await wait(280);
      await perform(1, battle.ai(1));
      if (battle.winner !== null || !battle.f[0].stunned) break;
      await wait(280);
      await perform(0, { type: "rest" });
    }
    if (battle.winner !== null) return finish();
    busy = false;
    refreshActions();
  }

  async function perform(side: Side, a: Action): Promise<void> {
    front = side;
    const events = battle.act(side, a);
    syncHud(side);
    for (const e of events) await animate(e);
    syncAll();
  }

  function damage(side: Side, n: number): void {
    shownHp[side] = Math.max(0, shownHp[side]! - n);
    syncHud(side);
  }

  function impact(target: Side, outcome: "miss" | "block" | "hit", dmg: number, crit: boolean, heavy: boolean): void {
    const D = actors[target];
    const t = now();
    const y = headY(target) - 10;
    if (outcome === "miss") {
      void D.play("dodge", t);
      fx.text(D.x, y, "MISS", "#ffffff");
      sfx.swoosh();
    } else if (outcome === "block") {
      void D.play("block", t);
      fx.sparks(D.x + D.facing * 50, midY(target));
      fx.text(D.x, y, `🛡 ${dmg}`, "#cfd8e0");
      sfx.block();
      damage(target, dmg);
    } else {
      void D.play("hit", t);
      D.flash(t);
      fx.feathers(D.x, midY(target), featherColor(target), crit ? 16 : heavy ? 11 : 6);
      fx.text(D.x, y, crit ? `CRIT ${dmg}!` : String(dmg), crit ? "#ffd84a" : "#ff6a5a", crit ? 48 : 38);
      fx.shake(crit ? 14 : heavy ? 11 : 5);
      if (crit) sfx.crit();
      else sfx.hit();
      cheerBoost = Math.min(1, cheerBoost + (crit ? 0.6 : 0.25));
      damage(target, dmg);
    }
  }

  async function animate(e: BattleEvent): Promise<void> {
    const name = (s: Side) => battle.f[s].name;
    switch (e.t) {
      case "move": {
        const A = actors[e.who];
        const forward = (e.to - e.from) * A.facing > 0;
        log(`${name(e.who)} ${forward ? "advances" : "falls back"}.`);
        sfx.step();
        fx.dust(A.x, GROUND, 4);
        await A.walkTo(cellX(e.to), now(), 0.25 + 0.15 * Math.abs(e.to - e.from));
        fx.dust(A.x, GROUND, 3);
        return;
      }
      case "swing": {
        const A = actors[e.who];
        const target = other(e.who);
        const anim = e.kind === "frenzy" ? "quick" : e.kind;
        const result = e.outcome === "miss" ? "Missed!" : e.outcome === "block" ? `Blocked! ${e.dmg} damage.` : `${e.crit ? "CRITICAL! " : ""}${e.dmg} damage!`;
        log(`${name(e.who)} ${VERBS[e.kind]}... ${result}`);
        sfx.swoosh();
        await A.play(anim, now(), () => impact(target, e.outcome, e.dmg, e.crit, e.kind === "power"));
        await wait(e.kind === "frenzy" ? 40 : 160);
        return;
      }
      case "egg": {
        const A = actors[e.who];
        const target = other(e.who);
        const D = actors[target];
        log(`${name(e.who)} hurls an Egg Grenade! ${e.outcome === "hit" ? `${e.dmg} damage!` : "It misses!"}`);
        let flight: Promise<void> = Promise.resolve();
        sfx.throw();
        await A.play("throw", now(), () => {
          const tx = e.outcome === "hit" ? D.x : D.x + D.facing * -90;
          const ty = e.outcome === "hit" ? midY(target) : GROUND;
          flight = fx.egg(A.x + A.facing * 30, headY(e.who) + 30, tx, ty, now());
        });
        await flight;
        sfx.splat();
        fx.yolk(e.outcome === "hit" ? D.x : D.x + D.facing * -90, e.outcome === "hit" ? midY(target) : GROUND);
        impact(target, e.outcome, e.dmg, false, true);
        await wait(300);
        return;
      }
      case "taunt": {
        const A = actors[e.who];
        const D = actors[other(e.who)];
        sfx.taunt();
        fx.shout(A.x, headY(e.who) - 30, A.facing, SHOUTS[Math.floor(Math.random() * SHOUTS.length)] ?? "BAWK!");
        await A.play("taunt", now());
        if (e.ok) {
          log(`${name(e.who)} taunts! ${name(other(e.who))} is rattled and loses energy.`);
          fx.text(D.x, headY(other(e.who)) - 10, "RATTLED!", "#ffb0f0", 30);
          sfx.cheer(0.22);
          cheerBoost = Math.min(1, cheerBoost + 0.5);
        } else {
          log(`${name(e.who)} taunts... the crowd boos.`);
          fx.text(A.x, headY(e.who) - 10, "BOO!", "#bbbbbb", 30);
          sfx.boo();
        }
        await wait(250);
        return;
      }
      case "roar": {
        const A = actors[e.who];
        const target = other(e.who);
        await A.play("roar", now(), () => {
          fx.shout(A.x + A.facing * 60, headY(e.who) - 40, A.facing, "COCK-A-DOODLE-DOO!");
          fx.shake(12);
          sfx.roar();
        });
        if (e.ok) {
          actors[target].stunned = true;
          fx.text(actors[target].x, headY(target) - 10, "STUNNED!", "#ffe14d", 32);
          log(`${name(e.who)} unleashes a Rooster Roar! ${name(target)} is stunned!`);
        } else {
          fx.text(actors[target].x, headY(target) - 10, "UNFAZED", "#cccccc", 28);
          log(`${name(e.who)} roars, but ${name(target)} holds firm.`);
        }
        await wait(300);
        return;
      }
      case "rest": {
        const A = actors[e.who];
        log(`${name(e.who)} catches their breath.`);
        sfx.rest();
        fx.snooze(A.x + 20, headY(e.who));
        fx.text(A.x - 30, headY(e.who) - 10, `+${e.en}⚡`, "#8fd8ff", 28);
        if (e.hp > 0) {
          fx.text(A.x + 40, headY(e.who) + 20, `+${e.hp}`, "#6be37a", 26);
          shownHp[e.who] = shownHp[e.who]! + e.hp;
        }
        await A.play("rest", now());
        syncHud(e.who);
        return;
      }
      case "heal": {
        const A = actors[e.who];
        log(`${name(e.who)} gets a Second Wind! +${e.hp} health.`);
        sfx.heal();
        fx.heal(A.x, midY(e.who));
        fx.text(A.x, headY(e.who) - 10, `+${e.hp}`, "#6be37a", 40);
        shownHp[e.who] = shownHp[e.who]! + e.hp;
        await A.play("heal", now());
        syncHud(e.who);
        return;
      }
      case "stunned": {
        log(`${name(e.who)} is stunned and loses a turn!`);
        fx.text(actors[e.who].x, headY(e.who) - 10, "💫 STUNNED", "#ffe14d", 28);
        await wait(800);
        actors[e.who].stunned = false;
        return;
      }
      case "ko": {
        const loser = actors[e.who];
        const winner = actors[other(e.who)];
        log(`${name(e.who)} is down! ${name(other(e.who))} wins!`);
        loser.knockOut(now());
        fx.feathers(loser.x, midY(e.who), featherColor(e.who), 30);
        fx.shake(18);
        sfx.ko();
        sfx.cheer(0.45);
        cheerBoost = 1;
        await wait(700);
        winner.state = "victory";
        await wait(500);
        return;
      }
    }
  }

  // ---------- intro & result ----------

  const banner = (text: string, cls = "") => {
    const b = h("div", { class: `banner ${cls}` }, text);
    document.getElementById("ui")?.append(b);
    setTimeout(() => b.remove(), 1100);
  };

  function intro(): HTMLElement {
    const card = h(
      "div",
      { class: "vs-card panel" },
      h(
        "div",
        { class: "sides" },
        h("div", null, h("div", { class: "who" }, me.name), h("div", { class: "ttl" }, `Level ${me.level} · ${me.wins} wins`)),
        h("div", { class: "vs" }, "VS"),
        h("div", null, h("div", { class: "who" }, `${opponent.boss ? "👑 " : ""}${opponent.name}`), h("div", { class: "ttl" }, `Level ${opponent.level} · ${opponent.title}`)),
      ),
      h("div", { class: "quote" }, `“${opponent.taunt.replaceAll("{name}", me.name)}”`),
      h(
        "div",
        { class: "row", style: { justifyContent: "center", gap: "16px", marginTop: "10px" } },
        btn("◀ Not Yet", toHub, "wood"),
        btn("FIGHT!", begin, "red big pulse"),
      ),
    );
    return card;
  }

  let introCard: HTMLElement | null = null;
  function begin(): void {
    if (!introCard) return;
    introCard.remove();
    introCard = null;
    banner("FIGHT!");
    sfx.cheer(0.35);
    cheerBoost = 0.8;
    busy = false;
    log(battle.dist() > 1 ? "Close the distance. Most weapons need you beak to beak." : "Fight!");
    syncAll();
  }

  async function finish(): Promise<void> {
    busy = true;
    refreshActions();
    await wait(600);
    const won = battle.winner === 0;
    const lines: [string, string][] = [];
    let next: () => void = toHub;
    let extra: string[] = [];
    let levels: number;
    if (won) {
      const hype = battle.f[0].hype;
      const xp = xpReward(opponent.level, true);
      const gold = goldReward(opponent.level, hype, !!opponent.boss);
      me.gold += gold;
      me.wins++;
      levels = gainXp(me, xp);
      lines.push(["Experience", `+${xp} XP`], ["Purse", `+${gold} gold`], ["Crowd bonus", `+${Math.round(hype / 2)}%`]);
      if (story) ({ lines: extra, next } = winStoryFight(opponent));
      else persist();
      sfx.fanfare();
    } else {
      const xp = xpReward(opponent.level, false);
      me.losses++;
      levels = gainXp(me, xp);
      lines.push(["Consolation XP", `+${xp} XP`]);
      persist();
      sfx.sad();
    }
    if (levels) setTimeout(() => sfx.levelup(), 900);
    const quote = won ? (DEFEAT_LINES[opponent.id] ?? "Ugh... you win this time.") : "Get back up, champ. Train, gear up, and try again.";
    const panel = h(
      "div",
      { class: "result panel" },
      h("h2", { class: won ? "" : "lost" }, won ? "VICTORY!" : "PLUCKED!"),
      h("div", { class: "quote" }, won ? `${opponent.name}: “${quote}”` : quote),
      h("div", { class: "lines" }, ...lines.map(([k, v]) => h("div", null, h("span", null, k), h("span", null, v))), ...extra.map((x) => h("div", null, h("span", null, `🏆 ${x}`)))),
      levels ? h("div", { class: "levelup" }, `LEVEL UP! Level ${me.level} · +${levels * 5} stat points`) : null,
      h(
        "div",
        { class: "row", style: { justifyContent: "center", gap: "16px", marginTop: "12px" } },
        ...(won
          ? [btn("Continue ▶", next, "green big")]
          : [btn("◀ The Roost", toHub, "wood"), btn("Rematch!", () => battleScreen({ opponent, backdrop, story }), "red big")]),
      ),
    );
    document.getElementById("ui")?.append(panel);
  }

  // ---------- keys ----------

  const keymap: Record<string, Action> = {
    arrowright: { type: "advance" },
    d: { type: "advance" },
    arrowleft: { type: "retreat" },
    a: { type: "retreat" },
    "1": { type: "attack", kind: "quick" },
    "2": { type: "attack", kind: "normal" },
    "3": { type: "attack", kind: "power" },
    t: { type: "taunt" },
    r: { type: "rest" },
  };
  known.forEach((id, i) => (keymap[String(4 + i)] = { type: "special", id }));

  introCard = intro();
  mount(
    (e) => {
      if (introCard) {
        if (e.key === "Enter" || e.key === " ") begin();
        return;
      }
      const a = keymap[e.key.toLowerCase()];
      if (a) {
        e.preventDefault();
        void onAction(a);
      }
    },
    huds[0].el,
    huds[1].el,
    logEl,
    actionBar,
    introCard,
    muteButton(true),
  );
  syncAll();
  logEl.style.opacity = "0";
}
